import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {Heart, MessageSquare, Video, Calendar, Music, BookOpen, Baby, Mail, TrendingUp, Users} from 'lucide-react'
import { lumi } from '../../lib/lumi'
import AdminLayout from '../../components/admin/AdminLayout'

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState({
    prayers: 0,
    testimonials: 0,
    sermons: 0,
    events: 0,
    worship: 0,
    bibleSchool: 0,
    kids: 0,
    contacts: 0,
    users: 0,
    pendingPrayers: 0,
    pendingTestimonials: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)
      try {
        const [prayers, testimonials, sermons, events, worship, bibleSchool, kids, contacts, users] = await Promise.all([
          lumi.entities.prayers.list({ limit: 1 }),
          lumi.entities.testimonials.list({ limit: 1 }),
          lumi.entities.sermons.list({ limit: 1 }),
          lumi.entities.events.list({ limit: 1 }),
          lumi.entities.worship_songs.list({ limit: 1 }),
          lumi.entities.bible_school_classes.list({ limit: 1 }),
          lumi.entities.kids_comics.list({ limit: 1 }),
          lumi.entities.contacts.list({ limit: 1 }),
          lumi.entities.users.list({ limit: 1 })
        ])

        const pendingPrayers = await lumi.entities.prayers.list({ filter: { status: 'PENDING' }, limit: 1 })
        const pendingTestimonials = await lumi.entities.testimonials.list({ filter: { status: 'PENDING' }, limit: 1 })

        setStats({
          prayers: prayers.total,
          testimonials: testimonials.total,
          sermons: sermons.total,
          events: events.total,
          worship: worship.total,
          bibleSchool: bibleSchool.total,
          kids: kids.total,
          contacts: contacts.total,
          users: users.total,
          pendingPrayers: pendingPrayers.total,
          pendingTestimonials: pendingTestimonials.total
        })
      } catch (error) {
        console.error('Failed to fetch stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const cards = [
    { label: 'Orações', value: stats.prayers, icon: Heart, color: 'bg-red-500', link: '/admin/prayers', pending: stats.pendingPrayers },
    { label: 'Depoimentos', value: stats.testimonials, icon: MessageSquare, color: 'bg-blue-500', link: '/admin/testimonials', pending: stats.pendingTestimonials },
    { label: 'Sermões', value: stats.sermons, icon: Video, color: 'bg-purple-500', link: '/admin/sermons' },
    { label: 'Eventos', value: stats.events, icon: Calendar, color: 'bg-green-500', link: '/admin/events' },
    { label: 'Louvores', value: stats.worship, icon: Music, color: 'bg-pink-500', link: '/admin/worship' },
    { label: 'Escola Bíblica', value: stats.bibleSchool, icon: BookOpen, color: 'bg-indigo-500', link: '/admin/bible-school' },
    { label: 'Programação', value: stats.kids, icon: Baby, color: 'bg-yellow-500', link: '/admin/programacao' },
    { label: 'Contatos', value: stats.contacts, icon: Mail, color: 'bg-orange-500', link: '/admin/contacts' },
    { label: 'Usuários', value: stats.users, icon: Users, color: 'bg-gray-500', link: '/admin/users' }
  ]

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a] mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando estatísticas...</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Visão geral do painel administrativo</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card) => {
          const Icon = card.icon
          return (
            <Link
              key={card.label}
              to={card.link}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${card.color} w-12 h-12 rounded-lg flex items-center justify-center text-white`}>
                  <Icon size={24} />
                </div>
                {card.pending !== undefined && card.pending > 0 && (
                  <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    {card.pending} pendente{card.pending > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className="text-gray-600 text-sm mb-1">{card.label}</p>
              <p className="text-3xl font-bold text-gray-900">{card.value}</p>
            </Link>
          )
        })}
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp size={24} className="text-[#1e3a8a]" />
          Resumo de Atividades
        </h2>
        <div className="space-y-3">
          {stats.pendingPrayers > 0 && (
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <span className="text-gray-700">Orações pendentes de aprovação</span>
              <span className="font-semibold text-red-600">{stats.pendingPrayers}</span>
            </div>
          )}
          {stats.pendingTestimonials > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-gray-700">Depoimentos pendentes de aprovação</span>
              <span className="font-semibold text-blue-600">{stats.pendingTestimonials}</span>
            </div>
          )}
          {stats.pendingPrayers === 0 && stats.pendingTestimonials === 0 && (
            <div className="text-center text-gray-500 py-4">
              Nenhuma ação pendente no momento
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

export default Dashboard